import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Typography,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { ErrorToaster, SuccessToaster } from "../../../components/Toaster";
import AuthServices from "../../../api/AuthServices/auth.index";
import Colors from "../../../assets/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../../context";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../assets/images";
import Loader from "../../../components/Loader";
import { useLocation } from "react-router-dom";
import { trackGtmEvent } from "../../../utils/gtm";

function AgentLogin() {
  const [showPassword, setShowPassword] = useState(false);
  const [loader, setLoader] = useState(false);
  const { WebUserLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  // Inside onSubmit:
  const onSubmit = async (data) => {
    setLoader(true);
    const obj = {
      email: data.email,
      password: data.password,
    };

    try {
      const response = await AuthServices.agentLogin(obj);
      WebUserLogin(response?.data?.user);
      trackGtmEvent("agent_login", { method: "agent_portal", email: data.email });
      SuccessToaster(response?.message);

      const hasActiveBookings = response?.data?.user?.activeBookings && response?.data?.user?.activeBookings.length > 0;
      if (hasActiveBookings) {
        localStorage.setItem("isOpenActiveBookingFeedbackForm", "true");
        navigate("/my-booking", { replace: true });
        setLoader(false);
        // SuccessToaster("You have active bookings pending feedback. Please complete the feedback form.");
        return;
      }

      const params = new URLSearchParams(location.search);
      const redirectPath = params.get("redirect") || "/";

      // Prevent going back to login page
      navigate(redirectPath, { replace: true });

      reset();
    } catch (error) {
      console.error(error);  // Log the error to check its structure
      ErrorToaster(error.response?.data?.message || error.message);
    } finally {
      setLoader(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f7fafc",
        px: 2,
        backgroundImage: `url(${Images.bgLogin})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
    >
      <Box
        component="img"
        onClick={() => navigate("/")}
        src={Images.RRLogo}
        alt="Talha's Estate"
        sx={{
          width: 300,
          position: "absolute",
          top: 20,
          left: 10,
          cursor: "pointer",
        }}
      />

      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{
          width: "100%",
          minHeight: "100vh",
        }}
      >
        <Grid item xs={12} sm={8} md={5}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            sx={{
              p: 4,
              backgroundColor: Colors.darkblue,
              borderRadius: 2,
              border: `1px solid ${Colors.primary}`,
            }}
          >
            {/* Header */}
            <Typography
              variant="h6"
              sx={{
                color: Colors.primary,
                fontWeight: 300,
                mb: 3,
                letterSpacing: "9px",
                fontFamily: "'Copperplate-Gothic-32-BC', sans-serif",
                fontSize: { xs: "1rem", md: "1.6rem" },
              }}
            >
              AGENT LOGIN
            </Typography>

            {/* Form */}
            <Box component="form" sx={{ width: "100%" }} onSubmit={handleSubmit(onSubmit)}>
              {/* Email */}
              <TextField
                placeholder="Email"
                fullWidth
                variant="standard"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Enter a valid email",
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderBottom: "1px solid #99a9bf",
                    color: "#99a9bf",
                    fontFamily: "'Gotham-Book',sans-serif",
                    mb: 3,
                    "& input": {
                      color: "#99a9bf",
                    },
                  },
                }}
              />

              {/* Password */}
              <TextField
                placeholder="Password"
                fullWidth
                type={showPassword ? "text" : "password"}
                variant="standard"
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 6,
                    message: "Minimum 6 characters required",
                  },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={{
                  disableUnderline: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        sx={{
                          color: "#99a9bf",
                          fontFamily: "'Gotham-Book',sans-serif",
                        }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    borderBottom: "1px solid #99a9bf",
                    color: "#99a9bf",
                    mb: 1,
                    "& input": {
                      color: "#99a9bf",
                    },
                  },
                }}
              />

              <br /><br />

              {/* Button */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                <Button
                  type="submit"
                  sx={{
                    color: "#b49068",
                    borderColor: Colors.primary,
                    border: "1px solid",
                    textTransform: "uppercase",
                    fontFamily: "'Gotham-Book', sans-serif",
                    fontWeight: 600,
                    letterSpacing: "3px",
                    borderRadius: "0",
                    py: 1,
                    px: 2,
                    width: "150px",
                    "&:hover": {
                      backgroundColor: Colors.primary,
                      color: Colors.darkblue,
                    },
                  }}
                >
                  {loader ? (
                    <Loader width="20px" height="20px" color={Colors.white} />
                  ) : (
                    "LOGIN"
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default AgentLogin;
